import { InputBase, Popper, alpha, autocompleteClasses } from '@mui/material';

import React from 'react';
import styled from 'styled-components';

import OpenAILogo from '@svg/company/openai';
import MetaLogo from '@svg/company/meta';
import GoogleLogo from '@svg/company/google';
import AnthropicLogo from '@svg/company/anthropic';
import DeepSeekLogo from '@svg/company/deepseek';
import GrokLogo from '@svg/company/grok';
import MistralLogo from '@svg/company/mistral';
import HuggingFaceLogo from '@svg/company/huggingface';

interface Props {
  anchorEl?: any;
  disablePortal?: boolean;
  open: boolean;
}

export type ListItemType = {
  id?: string;
  label: string;
  value: string;
  module_id?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: any;
};

export const StyledAutocompletePopper = styled.div`
  & .${autocompleteClasses.paper} {
    box-shadow: none;
    margin: 0;
    color: white;
    border-radius: 8px;
    font-size: 13px;
    border: 0 !important;
    background-color: ${({ theme }) => theme.background.standard} !important;
  }

  & .${autocompleteClasses.noOptions} {
    display: none;
  }

  & .${autocompleteClasses.listbox} {
    background-color: ${({ theme }) => theme.background.standard};
    border-radius: 8px;
    padding: 0;
    border: ${({ theme }) => `1px solid ${theme.line.light}`};

    ::-webkit-scrollbar {
      height: 8px;
      width: 8px;
    }

    ::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.scrollbar.track};
    }

    ::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.scrollbar.thumb};
    }

    & .${autocompleteClasses.option} {
      color: ${({ theme }) => theme.text.standard};
      min-height: auto;
      align-items: flex-start;
      padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};

      &:not(:last-of-type) {
        border-bottom: ${({ theme }) => `1px solid ${theme.line.lighter}`};
      }

      &[aria-selected='true'] {
        background-color: ${({ theme }) => alpha(theme.text.standard, 0.06)};
      }

      &:hover {
        background-color: ${({ theme }) => alpha(theme.text.standard, 0.06)};
        border-color: ${({ theme }) => `1px solid ${theme.line.light}`};
      }

      &.Mui-focused {
        background-color: ${({ theme }) => `${alpha(theme.text.standard, 0.06)} !important`};
      }

      &.Mui-focusVisible {
        background-color: ${({ theme }) => `${alpha(theme.text.standard, 0.06)} !important`};
      }
    }
  }

  & .${autocompleteClasses.groupLabel} {
    background-color: ${({ theme }) => theme.background.standard};
    color: ${({ theme }) => theme.text.subtext};
    font-size: 11px;
    height: 42px;
    font-weight: 400;
    font-family: 'Inter';
    border-top: ${({ theme }) => `1px solid ${theme.line.lighter}`};
  }

  &.${autocompleteClasses.popperDisablePortal} {
    position: relative;
  }
`;

export const AutocompletePopperPortal = styled(({ className, ...props }) => (
  <Popper {...props} className={className} />
))`
  min-width: 280px;
  & .MuiAutoComplete-paper {
    box-shadow: none;
    margin: 0;
    color: white;
    border-radius: 8px;
    font-size: 13px;
    border: 0 !important;
    background-color: ${({ theme }) => theme.background.standard} !important;
  }

  & .MuiAutocomplete-listbox {
    background-color: ${({ theme }) => theme.background.standard};
    border-radius: 8px;
    padding: 0;
    border: ${({ theme }) => `1px solid ${theme.line.light}`};

    & .MuiAutocomplete-option {
      color: ${({ theme }) => theme.text.standard};
      min-height: auto;
      align-items: flex-start;
      padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};

      &:not(:last-of-type) {
        border-bottom: ${({ theme }) => `1px solid ${theme.line.lighter}`};
      }

      &[aria-selected='true'] {
        background-color: ${({ theme }) => alpha(theme.text.standard, 0.06)};
      }

      &:hover {
        background-color: ${({ theme }) => alpha(theme.text.standard, 0.06)};
        border-color: ${({ theme }) => `1px solid ${theme.line.light}`};
      }

      &.Mui-focused {
        background-color: ${({ theme }) => `${alpha(theme.text.standard, 0.06)} !important`};
      }

      &.Mui-focusVisible {
        background-color: ${({ theme }) => `${alpha(theme.text.standard, 0.06)} !important`};
      }
    }
  }

  & .MuiAutocomplete-groupLabel {
    background-color: ${({ theme }) => theme.background.standard};
    color: ${({ theme }) => theme.text.subtext};
    font-size: 11px;
    height: 42px;
    font-weight: 400;
    font-family: 'Inter';
    border-top: ${({ theme }) => `1px solid ${theme.line.lighter}`};
  }

  & .MuiAutocomplete-noOptions {
    display: none;
  }
`;

export const AutocompleteContainer = styled.div<{ open: boolean }>`
  & .MuiPaper-root {
    border: ${({ open }) => (open ? '1px solid ##2f3744' : 0)};
  }
`;

export const AutocompletePopper: React.FC<Props> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disablePortal, anchorEl, open, ...other } = props;

  return <StyledAutocompletePopper {...other} />;
};

export const AutocompleteInput = styled(InputBase)<{ open: boolean; error?: boolean }>`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 8px;
  padding: 0px 13px;
  background-color: ${({ theme }) => theme.background.standard};
  font-size: 14;
  height: 40px;
  border-color: ${({ theme, open, error }) => (error ? theme.telnyx.error : open ? theme.primary : theme.line.light)};
  /* outline: ${({ theme, open }) => (open ? `1.5px solid ${theme.primary}` : 'none')}; */

  & .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.text.standard};
    font-weight: 400;
  }
`;

export const getInferenceProviderColour = (theme: any, model: string) => {
  if (model === 'openai') return theme.telnyx.green;
  if (model === 'meta-llama') return theme.telnyx.blue;
  if (model === 'mistralai') return theme.telnyx.orange;
  if (model === 'HuggingFaceH4') return theme.telnyx.orange;
  if (model === 'google') return '#80baf7';
  if (model === 'NousResearch') return theme.telnyx.purple;
  if (model === 'deepseek-ai') return theme.telnyx.blue;
  if (model === 'fixie-ai') return theme.telnyx.red;
  if (model === 'cognitivecomputations') return theme.telnyx.purple;
  if (model === 'anthropic') return theme.telnyx.orange;
  if (model === 'xai-org') return theme.text.standard;
  if (model === 'groq') return theme.telnyx.yellow;

  return theme.telnyx.green;
};

export const isPaidModel = (model: string) => {
  try {
    return (
      model.includes('openai') ||
      model.includes('anthropic') ||
      model.includes('xai-org') ||
      model.includes('gemini') ||
      model.includes('Groq')
    );
  } catch {
    return false;
  }
};

export const getInferenceProviderLogo = (model: string, color?: string) => {
  if (model === 'openai') return <OpenAILogo height={15} color={color} />;
  if (model === 'meta-llama') return <MetaLogo height={18} color={color} />;
  if (model === 'google') return <GoogleLogo height={18} color={color} />;
  if (model === 'anthropic') return <AnthropicLogo height={22} color={color} />;
  if (model === 'deepseek-ai') return <DeepSeekLogo height={24} color={color} />;
  if (model === 'xai-org') return <GrokLogo height={24} color={color} />;
  if (model === 'mistralai') return <MistralLogo height={16} color={color} />;
  if (model === 'fixie-ai') return <HuggingFaceLogo height={16} color={color} />;
  if (model === 'Groq') return <HuggingFaceLogo height={16} color={color} />;

  return <p>AI</p>;
};

export const isToolModel = (model: string) => {
  try {
    return (
      model.includes('openai') || model.includes('anthropic') || model.includes('Groq') || model.includes('meta-llama')
    );
  } catch {
    return false;
  }
};
